import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  createStyles,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import { ArrowBack, Close, CloudDownload } from '@material-ui/icons';
import moment from 'moment';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

import L from '../../utils/get-leaflet';
import DateRangeField from '../common/date-range-field';
import { DateRange } from '../common/date-range-picker/types';
import MultiselectField from '../common/multiselect-field';
import { markerIcons } from '../../utils/map-marker-icons';
import FieldView from '../common/field-view';
import { useDateIntoCSV } from '../../hooks/use-data-into-csv';
import AutocompleteField from '../common/autocomplete-field';
import { useAutocomplete } from '../../hooks/use-autocomplete';
import { useGuide } from '../../hooks/use-guide';
import { useSelection } from '../common/map/hooks/use-selection';
import SelectionTool from '../common/map/controls/selection-tool';
import {
  EToolActions,
  useToolActionState,
} from '../common/map/hooks/use-tool-action-state';
import { OutletPoint } from '../../api/models/outlet-point';
import UniverseIcon from '../common/icons/universe';
import { useRectDraw } from '../common/map/hooks/use-rect-draw';
import { OutletCategory } from '../universe/map';

type MapProps = WithStyles<typeof styles>;

require('leaflet.markercluster');

const MapView: FC<MapProps> = (props) => {
  const { classes } = props;

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const [goodClusterGroup, setGoodClusterGroup] = useState<L.LayerGroup | null>(
    null
  );
  const [badClusterGroup, setBadClusterGroup] = useState<L.LayerGroup | null>(
    null
  );

  const { selected, deselectAll, isOneSelected, isSelected, onSelect } =
    useSelection();

  const { action, onAction } = useToolActionState();

  const [markers, setMarkers] = useState<L.Marker[]>([]);
  const [layers, setLayers] = useState<'both' | 'good' | 'bad'>('good');

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: moment().subtract(1, 'month').startOf('month'),
    endDate: moment(),
  });
  const [salesRepresentatives, setSalesRepresentatives] = useState<
    EntityBase[]
  >([]);
  const [minOrderThreshold, setMinOrderThreshold] = useState<number>(1000);
  const [outletPoints, setOutletPoints] = useState<Array<OutletPoint>>([]);

  const [selectedCategories, setSelectedCategories] = useState<
    OutletCategory[]
  >([]);
  const [selectedTypes, setSelectedTypes] = useState<EntityBase[]>([]);
  const [city, setCity] = useState<EntityBase | null>(null);

  const { rectRef: selectionRef } = useRectDraw({
    map,
    canDraw: action === EToolActions.SELECTION,
    onFinish: (selectingBounds) => {
      onSelect(
        markers.reduce<Array<number>>((acc, marker, index) => {
          if (selectingBounds.contains(marker.getLatLng())) {
            marker.setIcon(markerIcons['outlet-selected']);
            //if(goodClusterGroup.hasLayer(marker)) goodClusterGroup.
            return [...acc, index];
          }
          return acc;
        }, [])
      );
    },
    removeOnFinish: true,
  });

  const { rectRef: areaRef, rect } = useRectDraw({
    map,
    canDraw: action === EToolActions.SELECT_AREA,
    onFinish: () => {},
  });

  const { onDownload } = useDateIntoCSV<OutletPoint>(
    'outlets.csv',
    new Map([
      ['id', 'ID'],
      ['country', 'Страна'],
      ['region', 'Регион'],
      ['city', 'Город'],
      ['name', 'Наименование'],
      ['employee', 'Торговый представитель'],
      ['address', 'Адрес'],
      ['latlng', 'Координаты'],
      ['is_good', 'Выше минимума '],
      ['order_sum', 'Сумма последних заказов'],
    ]),
    new Set(['orders'])
  );

  const onSearch = () => {
    deselectAll();
    // dispatch(
    //   mapActions.fetchGoodBadPoints({
    //     begin_date: moment(dateRange?.startDate).format('YYYY-MM-DDT00:00:00'),
    //     end_date: moment(dateRange?.endDate).format('YYYY-MM-DDT00:00:00'),
    //     min_order_threshold: minOrderThreshold,
    //     employees: salesRepresentatives.map((item) => '' + item.id),
    //     category_id:
    //       selectedCategories.length > 0
    //         ? selectedCategories.map((item) => item.id?.toString() || '')
    //         : undefined,
    //     type_id:
    //       selectedTypes.length > 0
    //         ? selectedTypes.map((item) => item.id?.toString() || '')
    //         : undefined,
    //     city_id: city?.id?.toString(),
    //   })
    // ).then((response) => {
    //   goodClusterGroup && goodClusterGroup.clearLayers();
    //   badClusterGroup && badClusterGroup.clearLayers();
    //   setOutletPoints(
    //     (response.payload as Paginated<'data', OutletPoint>).data
    //   );
    // });
  };

  useEffect(() => {
    if (!map) {
      return;
    }
    if (action === EToolActions.NOOP) {
      map.dragging.enable();
    } else {
      map.dragging.disable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  useEffect(() => {
    if (mapContainerRef.current && !map) {
      const _map = L.map(mapContainerRef.current).setView([55.8, 37.8], 9);
      L.tileLayer('http://tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(
        _map
      );

      const _goodClusterGroup = L.markerClusterGroup({
        // @ts-ignore
        iconCreateFunction(cluster) {
          return L.divIcon({
            html:
              '<div class="' +
              classes.good_cluster +
              '">' +
              cluster.getChildCount() +
              '</div>',
          });
        },
        animate: true,
      });
      const _badClusterGroup = L.markerClusterGroup({
        // @ts-ignore
        iconCreateFunction(cluster) {
          return L.divIcon({
            html:
              '<div class="' +
              classes.bad_cluster +
              '">' +
              cluster.getChildCount() +
              '</div>',
          });
        },
        animate: true,
      });
      setGoodClusterGroup(_goodClusterGroup);
      setBadClusterGroup(_badClusterGroup);
      _goodClusterGroup.addTo(_map);
      setMap(_map);
    }
  }, [classes.bad_cluster, classes.good_cluster, map]);

  useEffect(() => {
    if (!map || !goodClusterGroup || !badClusterGroup) {
      return;
    }
    map.removeLayer(goodClusterGroup);
    map.removeLayer(badClusterGroup);
    if (layers === 'both' || layers === 'good') {
      goodClusterGroup.addTo(map);
    }
    if (layers === 'both' || layers === 'bad') {
      badClusterGroup.addTo(map);
    }
  }, [goodClusterGroup, badClusterGroup, map, layers]);

  useEffect(() => {
    markers.forEach((marker, index) => {
      if (!isSelected(index)) {
        marker.setIcon(
          markerIcons[
            outletPoints[index]?.is_good ? 'outlet-positive' : 'outlet-negative'
          ]
        );
      }
    });
    if (map && isOneSelected) {
      map.flyTo(outletPoints[selected[0]].latlng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneSelected, setMarkers, selected, markers, map, outletPoints]);

  useEffect(() => {
    if (!map || !goodClusterGroup || !badClusterGroup) {
      return;
    }
    setMarkers(
      outletPoints.map((item, index) => {
        const marker = L.marker(item.latlng, {
          icon: markerIcons[
            item.is_good ? 'outlet-positive' : 'outlet-negative'
          ],
        });
        if (item.is_good) {
          goodClusterGroup.addLayer(marker);
        } else {
          badClusterGroup.addLayer(marker);
        }

        marker.on('click', () => {
          onSelect([index]);
          marker.setIcon(markerIcons['outlet-selected']);
        });
        return marker;
      })
    );
  }, [
    setMarkers,
    onSelect,
    outletPoints,
    goodClusterGroup,
    badClusterGroup,
    map,
  ]);

  const onDownloadView = () => {
    if (!map) {
      return;
    }
    const bbox = map.getBounds();
    onDownload(
      outletPoints.reduce<OutletPoint[]>((acc, item, index) => {
        if (bbox.contains(markers[index].getLatLng())) {
          acc.push(item);
        }
        return acc;
      }, [])
    );
  };

  const history = useHistory();

  const gotoUniverse = () => {
    if (rect) {
      history.push(
        `/smartsale/universe/${rect.getNorthWest().lat};${
          rect.getNorthWest().lng
        };${rect.getSouthEast().lat};${rect.getSouthEast().lng}`
      );
    }
  };

  return (
    <>
      <div
        ref={mapContainerRef}
        className={clsx([
          classes.mapContainer,
          ...(action === EToolActions.SELECTION ? ['selection-action'] : []),
        ])}
      />
      {action === EToolActions.SELECTION && (
        <div className={classes.selectionPane} ref={selectionRef} />
      )}
      {action === EToolActions.SELECT_AREA && (
        <div className={classes.areaPane} ref={areaRef}>
          <IconButton
            color="primary"
            title="Перейти во вселенную"
            onClick={gotoUniverse}
          >
            <UniverseIcon />
          </IconButton>

          <Box display="flex" mb={1}>
            <ArrowBack color="action" />
            <Typography color="textPrimary">К Вселенной</Typography>
          </Box>
        </div>
      )}

      <div className={classes.controls}>
        <Card className={classes.layers}>
          <FormControlLabel
            control={
              <Switch
                name="checkedB"
                color="primary"
                checked={layers === 'good' || layers === 'both'}
                onChange={() =>
                  setLayers(
                    layers === 'both'
                      ? 'bad'
                      : layers === 'bad'
                      ? 'both'
                      : 'bad'
                  )
                }
              />
            }
            label="Заказы выше минимума"
          />
          <FormControlLabel
            control={
              <Switch
                name="checkedB"
                color="primary"
                checked={layers === 'bad' || layers === 'both'}
                onChange={() =>
                  setLayers(
                    layers === 'both'
                      ? 'good'
                      : layers === 'good'
                      ? 'both'
                      : 'good'
                  )
                }
              />
            }
            label="Заказы ниже минимума"
          />
        </Card>
        <Card className={classes.filter}>
          <Box padding={2} display="flex" flexDirection="column">
            <Box pb={2}>
              <DateRangeField
                id='period'
                value={dateRange}
                onChange={setDateRange}
                label="Период"
              />
            </Box>
            <Box mb={2}>
              <AutocompleteField
                id="city"
                name="cities"
                label="Город"
                onFetchOptions={useAutocomplete()}
                onChange={(value) =>
                  !Array.isArray(value) ? setCity(value) : setCity(null)
                }
              />
            </Box>
            {/* <Box pb={2}>
              <MultiselectField
                id="filter"
                name="sales_representative"
                label="Торговый представитель"
                value={salesRepresentatives}
                onChange={(values) => {
                  setSalesRepresentatives(values || []);
                }}
                onFetchOptions={() =>
                  dispatch(
                    guidesActions.fetchGuides({ name: 'sales_representative' })
                  ).then(({ payload, meta }) => {
                    if (meta.requestStatus === 'fulfilled') {
                      return (payload as EntityBase[]) || [];
                    } else {
                      return [];
                    }
                  })
                }
              />
            </Box> */}
            {/* <Box mb={2}>
              <MultiselectField
                id="category"
                name="category"
                label="Категории ТТ"
                onFetchOptions={useGuide('outlet_category')}
                onChange={(categories) => {
                  setSelectedCategories(
                    categories
                      ? Array.isArray(categories)
                        ? categories
                        : [categories]
                      : []
                  );
                }}
              />
            </Box> */}
            <Box mb={2}>
              <MultiselectField
                id="types"
                name="type"
                label="Типы ТТ"
                value={selectedTypes}
                onFetchOptions={useGuide('outlet_type')}
                onChange={(types) => {
                  setSelectedTypes(types || []);
                }}
              />
            </Box>
            <Box pb={2}>
              <TextField
                label="Сумма минимального заказа (руб)"
                type="number"
                variant="outlined"
                fullWidth
                value={minOrderThreshold}
                onChange={(event) =>
                  setMinOrderThreshold(parseInt(event.target.value))
                }
              />
            </Box>
            <Box display="flex">
              <Box flexGrow={1} mr={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={onSearch}
                >
                  Найти
                </Button>
              </Box>
              <IconButton
                onClick={onDownloadView}
                disabled={outletPoints.length === 0}
              >
                <CloudDownload />
              </IconButton>
            </Box>
            {outletPoints.length > 0 && (
              <Typography>
                Найдено торговых точек: {outletPoints.length}{' '}
              </Typography>
            )}
          </Box>
        </Card>
        <SelectionTool active={true} action={action} onAction={onAction} />
        {isOneSelected && (
          <Card className={classes.pointInfo}>
            <CardHeader
              avatar={
                <Avatar
                  aria-label="recipe"
                  className={[
                    classes.avatar,
                    ...(outletPoints[selected[0]].is_good ? ['good'] : ['bad']),
                  ].join(' ')}
                  color={
                    outletPoints[selected[0]].is_good ? '#18d300' : '#e20909'
                  }
                >
                  {outletPoints[selected[0]].name?.slice(0, 1) || ''}
                </Avatar>
              }
              action={
                <IconButton aria-label="settings" onClick={deselectAll}>
                  <Close />
                </IconButton>
              }
              title={outletPoints[selected[0]].name}
              subheader={
                outletPoints[selected[0]].city +
                ' ' +
                outletPoints[selected[0]].full_address
              }
            />
            <CardContent>
              <FieldView
                title="Торговый представитель"
                value={outletPoints[selected[0]].employee.name}
              />
              <Typography color="textSecondary" component="p">
                Сумма заказов за период:{' '}
                <span
                  className={[
                    classes.order_sum,
                    ...(outletPoints[selected[0]].is_good ? ['good'] : ['bad']),
                  ].join(' ')}
                >
                  {outletPoints[selected[0]].order_sum} ₽
                </span>
              </Typography>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    mapContainer: {
      width: '100%',
      height: '100%',
      margin: '-16px',
      position: 'absolute',
      cursor: 'grab',
      '& .leaflet-right': {
        zIndex: 1001,
      },
      '& .leaflet-div-icon': {},
      '&.selection-action': {
        cursor: 'crosshair',
      },
    },
    selectionPane: {
      position: 'absolute',
      zIndex: 999,
      border: '1px dashed #0064FF',
      backgroundColor: 'rgba(0, 100, 255, 0.2)',
    },
    areaPane: {
      position: 'absolute',
      zIndex: 999,
      border: '1px dashed #0064FF',
      backgroundColor: 'rgba(0, 255, 100, 0.2)',
      display: 'flex',
      alignItems: 'flex-end',
    },
    controls: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      zIndex: 1000,
      width: 345,
    },
    layers: {
      overflow: 'initial',
      width: 345,
      padding: '10px',
    },
    filter: {
      marginTop: '10px',
      overflow: 'initial',
      width: 345,
    },
    pointInfo: {
      marginTop: '10px',
      width: 345,
    },
    order_sum: {
      color: theme.palette.primary.main,
    },
    media: {
      height: 0,
      paddingTop: '56.25%', // 16:9
    },
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    avatar: {
      backgroundColor: theme.palette.primary.main,
    },
    good_cluster: {
      backgroundColor: '#18d300',
      height: '30px',
      width: '30px',
      textAlign: 'center',
      border: '1px solid white',
      borderRadius: '50%',
      boxShadow: '1px 1px 3px gray',
      marginTop: '-15px',
      paddingTop: '5px',
      fontWeight: 'bold',
      color: 'white',
    },
    bad_cluster: {
      backgroundColor: '#e20909',
      height: '30px',
      width: '30px',
      textAlign: 'center',
      border: '1px solid white',
      borderRadius: '50%',
      boxShadow: '1px 1px 3px gray',
      marginTop: '-15px',
      paddingTop: '5px',
      fontWeight: 'bold',
      color: 'white',
    },
  });

export default withStyles(styles)(MapView);
